import {
    Container,
    Typography,
    TextField,
    Grid,
    FormControlLabel,
    Select,
    Card,
    CardContent,
    FormHelperText,
    Switch,
    Avatar,
    Button,
    MenuItem,
    Tooltip,
    Box,
    IconButton,
    Stack,
    Fade,
    Link
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { When } from 'react-if';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';

import { Page } from '../../../components';
import { CustomerTypes, SystemUserTypes } from '../../../types';
import { settingsFormSchema, settingsFormSchemaWithoutDispatch } from '../../../schemas/settings.schema';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import { useGenericMutation } from '../../../services/react-query/generic.mutation';
import { handleApiError } from '../../../services/react-query/react-query.client';
import { RootState } from '../../../store';
import { MySubscription } from '../../../components/subscription-card';
import { CustomerService } from '../../../services/customer.service';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { ChangeOrganizationOwnerModal } from '../../../components/change-organization-owner-modal';
import { CheckOnMeFlowDiagram } from '../../../components/check-on-me-flow-diagram';
import { createSystemUserPermissionCheck } from '../../../helpers/permissionCheck';
import { formatUsPhone, isValidUsPhone } from '../../../helpers/phone';
import { EmailChipsInput } from '../../../components/email-chips-input';
import { useNotificationRulesEnabled } from '../../../components/notification-rules/use-notification-rules-enabled';
import { CHECK_ON_ME_WHEN } from '../../../types/notification-rules.types';

export const SettingsScreen = () => {
    const [changeOwnerModalOpen, setChangeOwnerModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { authUser } = useSelector((state: RootState) => state.auth);

    const notificationOptions = ["NONE", "EXPIRED", "EXPIRED_ESCALATED", "ESCALATED", "ALL"];

    // Check if user can edit settings
    const canEditSettings = createSystemUserPermissionCheck(customerPermissionFlags.SUPER_ADMIN);

    // Customer details + subscriptions come from a fresh GET on every mount.
    // We deliberately do NOT read this from redux: in POM-master mode the
    // shared `state.customer.details` slot is the last masqueraded customer's
    // data and can leak across navigations (e.g. Akron showing BMC's
    // location after a masquerade-stop mid-flow). Owning the data locally
    // here makes it impossible for this page to display a different
    // customer's settings than the one the API just returned for the
    // current session.
    // cacheTime: 0 + staleTime: 0 → entry is evicted on unmount and refetched
    // on next mount with no stale-while-revalidate flash of the previous
    // customer's data.
    const { data: details, refetch: refetchDetails } = useGenericQuery<CustomerTypes.GetCustomerDetailsResponse>(
        'getCustomerDetails',
        'customer/details',
        undefined,
        0,
        false,
        0,
    );

    const {
        refetch,
        data: settings,
        isLoading,
        isFetching,
    } = useGenericQuery<CustomerTypes.OnboardingForm>(
        'getCustomerSettings',
        'customer/getSettings',
        undefined,
        0,
        false,
        0,
    );

    const { mutateAsync: updateSettings, isLoading: isUpdateLoading } =
        useGenericMutation<CustomerTypes.OnboardingForm, boolean>(
            'updateSettings',
            `customer/updateSettings/${details?.customer?.id}`,
            'put'
        );

    const notificationRulesOn = useNotificationRulesEnabled();

    const formik = useFormik<CustomerTypes.OnboardingForm>({
        initialValues: settings
            ? settings
            : ({} as CustomerTypes.OnboardingForm),
        enableReinitialize: true,
        validationSchema: notificationRulesOn ? settingsFormSchemaWithoutDispatch : settingsFormSchema,
        onSubmit: async (values) => {
            if (values.alertsRetention) {
                if (values.alertsRetention < 1) {
                    toast('Alerts retention cannot be less than 1.', {
                        autoClose: 1500,
                        type: 'error',
                    });
                    return;
                }

                if (values.alertsRetention > 365) {
                    toast('Alerts retention cannot be more than 365.', {
                        autoClose: 1500,
                        type: 'error',
                    });
                    return;
                }
            }

            if (values.reportsRetention) {
                if (values.reportsRetention < 1) {
                    toast('Reports retention cannot be less than 1.', {
                        autoClose: 1500,
                        type: 'error',
                    });
                    return;
                }

                if (values.reportsRetention > 365) {
                    toast('Reports retention cannot be more than 365.', {
                        autoClose: 1500,
                        type: 'error',
                    });
                    return;
                }
            }

            // For organizations on Notification Rules the Notifications page owns
            // Notify On Dispatch. Leaving these out means a Settings save never
            // writes back a copy loaded earlier; the API skips any absent field.
            const {
                callOnDispatch: _callOnDispatch,
                callOnDispatchPhoneNumbers: _callOnDispatchPhoneNumbers,
                callOnDispatchEmails: _callOnDispatchEmails,
                ...withoutDispatch
            } = values;
            const payload = notificationRulesOn ? withoutDispatch : values;

            await updateSettings(payload as CustomerTypes.OnboardingForm, {
                onError: (e) => {
                    const msg = handleApiError(e);

                    toast(msg, {
                        autoClose: 1500,
                        type: 'error',
                    });
                },
                onSuccess: async () => {
                    await refetch();

                    toast('Settings successfully updated.', {
                        autoClose: 1500,
                        type: 'success',
                    });

                    // setDisabled(true); // REMOVE
                    setHasUnsavedChanges(false);
                },
            });
        },
    });

    const { setValues, getFieldProps, submitForm, setFieldValue, touched, errors, values } = formik;

    // Track unsaved changes
    useEffect(() => {
        if (settings && canEditSettings) {
            const hasChanges = JSON.stringify(values) !== JSON.stringify(settings);
            setHasUnsavedChanges(hasChanges);
        } else {
            setHasUnsavedChanges(false);
        }
    }, [values, settings, canEditSettings]);

    // Helper: mark unsaved changes on any field change
    const handleFieldChange = (field: string, value: any) => {
        setFieldValue(field, value);
        if (canEditSettings) setHasUnsavedChanges(true);
    };

    // (Removed CustomerService.getCustomerDetails() mount-effect.) We now
    // fetch via useGenericQuery above with staleTime=0, so the GET fires on
    // every mount without going through the redux-dispatching service path.

    useEffect(() => {
        if (settings) {
            setValues(settings);
        }
    }, [settings]);

    return (
        <Page title={'POM Safe - Settings'}>
            {/* No Edit Permission Banner */}
            {!canEditSettings && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: {
                            xs: "5vw",
                            sm: "5vw",
                            md: "5vw",
                            lg: "calc(280px + 5vw)",
                            xl: "calc(280px + 5vw)",
                        },
                        right: 0,
                        zIndex: 1201,
                        py: 1,
                        px: 2,
                        textAlign: 'center',
                    }}
                >
                    <Container maxWidth="md" sx={{ mt: 1 }}>
                        <Grid container gap={2}>
                            <Typography variant="body2" color="warning.contrastText" fontWeight={600}>
                                You do not have permission to edit these settings. Only Super Admins can make changes.
                            </Typography>
                        </Grid>
                    </Container>
                </Box>
            )}
            {/* Unsaved Changes Banner */}
            <Fade in={hasUnsavedChanges} timeout={{ enter: 400, exit: 400 }}>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 75,
                        zIndex: 9999,
                        py: 1,
                        px: 2,
                    }}
                >
                    <Container maxWidth="md" sx={{ mt: 1 }}>
                        <Grid container gap={2}>
                            <LoadingButton
                                onClick={() => {
                                    const errs = Object.values(errors);
                                    if (errs.length > 0) {
                                        const errorMessages = errs.join('\n');
                                        toast(errorMessages, {
                                            autoClose: 1500,
                                            type: 'error',
                                        });
                                    } else {
                                        submitForm();
                                    }
                                }}
                                loading={isUpdateLoading}
                                disabled={!canEditSettings || isUpdateLoading}
                                sx={{ fontWeight: 600, height: 40 }}
                                size="large"
                                variant="contained"
                            >
                                Save Changes
                            </LoadingButton>
                            <Button
                                variant="contained"
                                color="inherit"
                                size="large"
                                sx={{ mr: 1, height: 40 }}
                                onClick={() => {
                                    if (settings) {
                                        setValues(settings);
                                    }
                                    setHasUnsavedChanges(false);
                                }}
                                disabled={isUpdateLoading || !canEditSettings}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Container>
                </Box>
            </Fade>

            <When condition={!isLoading && !isFetching}>
                <Container maxWidth={'lg'} >
                    <Card>
                        <CardContent>
                            <Grid item xs={12} md={12}>
                                <Grid container spacing={2}>
                                    {(details?.subscriptions?.length ?? 0) > 0 &&
                                        details?.subscriptions?.map((sub) => {
                                            return (
                                                <Grid item xs={12} md={12} key={sub.id}>
                                                    <MySubscription
                                                        subscription={sub}
                                                        customer={details.customer}
                                                        onSubscriptionChanged={() => { void refetchDetails(); }}
                                                    />
                                                </Grid>
                                            );
                                        })}
                                </Grid>
                            </Grid>

                            <Grid container gap={1} flexDirection={'column'} pr={1}>
                                <Grid
                                    display={'flex'}
                                    flexDirection={'row'}
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                >
                                    <Typography
                                        fontWeight={500}
                                        fontSize={18}
                                        color={'#000000'}
                                    >
                                        Organization Owner
                                    </Typography>
                                </Grid>


                                <Grid container flexDirection='row' gap={2}>
                                    {details?.customer?.contactEmail ? (
                                        <Avatar 
                                            key={details?.customer?.contactEmail} 
                                            alt={`${details?.customer?.contactFirstName} ${details?.customer?.contactLastName}`}
                                            sx={{ bgcolor: 'background.neutral', color: '#1976D2' }}
                                        >
                                            {`${details?.customer?.contactFirstName?.charAt(0) || ''}${details?.customer?.contactLastName?.charAt(0) || ''}`}
                                        </Avatar>
                                    ) : (
                                        <Avatar></Avatar>
                                    )}

                                    <Grid item flexDirection={'column'}>
                                        <Typography fontWeight={600}>
                                            {`${details?.customer?.contactFirstName || ''} ${details?.customer?.contactLastName || ''}`.trim()}
                                        </Typography>

                                        <Typography fontSize={14} color="textSecondary">
                                            {details?.customer?.contactEmail}
                                        </Typography>
                                    </Grid>

                                    <Tooltip title={canEditSettings ? "" : "Only Super Admins can change organization owner."}>
                                        <Button 
                                            sx={{ bgcolor: "background.neutral", color: "text.secondary", fontSize: 12, padding: 2, height: 24, my: "auto" }}
                                            onClick={() => setChangeOwnerModalOpen(true)}
                                            disabled={!canEditSettings}
                                        >
                                            Change
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            <Grid
                                marginBottom={1}
                                marginTop={2}
                                display={'flex'}
                                flexDirection={'row'}
                                justifyContent={'space-between'}
                            >
                                <Typography
                                    fontWeight={500}
                                    fontSize={18}
                                    color={'#000000'}
                                >
                                    General Settings
                                </Typography>
                            </Grid>

                            {/* SOS Passcode */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.passCode !== "" && values?.passCode !== undefined && values?.passCode !== null}
                                            onChange={(e) => {
                                                if (!e.target.checked) {
                                                    handleFieldChange('passCode', "");
                                                } else {
                                                    // When turning on, set focus to the text field so user can enter passcode
                                                    // The field will be enabled due to the switch being on
                                                }
                                            }}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 2/3 }}>
                                    <Typography fontWeight="600" fontSize={16}>SOS Passcode</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Set a passcode that your team will share with POM Safe Dispatch when testing or to confirm they're safe in the event of an SOS Alert or Check-on-Me escalation
                                    </Typography>
                                </Grid>
                                <TextField
                                    sx={{ width: 150, my: "auto" }}
                                    size="medium"
                                    type="text"
                                    placeholder="Enter passcode"
                                    {...getFieldProps('passCode')}
                                    value={values?.passCode}
                                    error={Boolean(touched.passCode && errors.passCode)}
                                    helperText={touched.passCode && errors.passCode}
                                    disabled={!canEditSettings}
                                    onChange={(e) => handleFieldChange('passCode', e.target.value)}
                                />
                            </Grid>

                            {/* Notify On Dispatch. Organizations on Notification Rules edit it on
                                the Notifications page, the only place in the Safety Hub that does, so
                                this shows a pointer there. Everyone else keeps the editor here. */}
                            {notificationRulesOn ? (
                                <Grid container sx={{ width: 800, borderRadius: 2, padding: 2, border: 1, flexDirection: "column", borderColor: "divider", mt: 3 }}>
                                    <Typography fontWeight="600" fontSize={16}>Notify On Dispatch</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Who POM calls or emails when EMS is sent to an SOS is now set on the{' '}
                                        <Link component={RouterLink} to="/settings/notifications" underline="hover">
                                            Notifications page
                                        </Link>
                                        .
                                    </Typography>
                                </Grid>
                            ) : (
                            <Grid container sx={{ width: 800, borderRadius: 2, padding: 2, border: 1, flexDirection: "column", borderColor: "divider", mt: 3 }}>
                                <Grid container direction="row">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values?.callOnDispatch ?? false}
                                                onChange={(e) => handleFieldChange('callOnDispatch', e.target.checked)}
                                                disabled={!canEditSettings}
                                            />
                                        }
                                        label=""
                                        sx={{ my: "auto" }}
                                    />
                                    <Grid maxWidth="sm" sx={{ width: 1 }}>
                                        <Typography fontWeight="600" fontSize={16}>Notify On Dispatch</Typography>
                                        <Typography color="textSecondary" variant="body2">
                                            When enabled, an automated phone call will be placed to each phone number below, and a dispatch summary email will be sent to each email address below, whenever an SOS alert is dispatched. Add at least one phone number or email address.
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {values?.callOnDispatch && (
                                    <Box sx={{ mt: 2, ml: 6 }}>
                                        <Typography fontSize={13} fontWeight={600} mb={1} color="text.secondary">
                                            Phone Numbers
                                        </Typography>
                                        <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
                                            {(values?.callOnDispatchPhoneNumbers ?? []).map((phone, index) => {
                                                const showError = !isValidUsPhone(phone);
                                                return (
                                                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                                                        <TextField
                                                            size="small"
                                                            value={phone || '+1'}
                                                            onChange={(e) => {
                                                                const next = [...(values?.callOnDispatchPhoneNumbers ?? [])];
                                                                next[index] = formatUsPhone(e.target.value);
                                                                handleFieldChange('callOnDispatchPhoneNumbers', next);
                                                            }}
                                                            disabled={!canEditSettings}
                                                            error={showError}
                                                            helperText={showError ? 'Must be +1 followed by 10 digits' : ''}
                                                            fullWidth
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                const next = (values?.callOnDispatchPhoneNumbers ?? []).filter((_, i) => i !== index);
                                                                handleFieldChange('callOnDispatchPhoneNumbers', next);
                                                            }}
                                                            disabled={!canEditSettings}
                                                            aria-label="remove phone number"
                                                        >
                                                            <ClearIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleFieldChange(
                                                'callOnDispatchPhoneNumbers',
                                                [...(values?.callOnDispatchPhoneNumbers ?? []), '+1']
                                            )}
                                            disabled={!canEditSettings}
                                            sx={{ mt: 1.5 }}
                                        >
                                            Add Phone Number
                                        </Button>
                                        {/* Hidden until the API returns the field: a settings
                                            save against an older API would silently strip any
                                            emails entered here. */}
                                        {values?.callOnDispatchEmails !== undefined && (
                                            <>
                                                <Typography fontSize={13} fontWeight={600} mt={2.5} mb={1} color="text.secondary">
                                                    Email Addresses
                                                </Typography>
                                                <Box sx={{ maxWidth: 420 }}>
                                                    <EmailChipsInput
                                                        value={values?.callOnDispatchEmails ?? []}
                                                        onChange={(emails) => handleFieldChange('callOnDispatchEmails', emails)}
                                                        label=""
                                                        placeholder="Type an address; separate with a comma or semicolon"
                                                        disabled={!canEditSettings}
                                                        helperText="Each address receives a dispatch summary email."
                                                    />
                                                </Box>
                                            </>
                                        )}
                                        {typeof errors.callOnDispatchPhoneNumbers === 'string' && (
                                            <FormHelperText error sx={{ mt: 1.5 }}>
                                                {errors.callOnDispatchPhoneNumbers}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            )}

                            {/* View POM User Location */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.mapSettingsDeviceEnabled ?? true}
                                            onChange={(e) => handleFieldChange('mapSettingsDeviceEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>View POM User Location</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Disable POM Users Device & Mobile App location on the Event Map
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Enable Safety Search */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.safetyReportEnabled ?? true}
                                            onChange={(e) => handleFieldChange('safetyReportEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Enable Safety Search</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Allow Mobile App users to search an address to review Sex Offender & Crime data for an address
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Reports Button (Mobile Map) */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.reportsButtonEnabled ?? true}
                                            onChange={(e) => handleFieldChange('reportsButtonEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Reports Button (Mobile Map)</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Show or hide the Reports button on the mobile app map screen.
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Add Report Button (Mobile Reports Drawer) */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.addReportButtonEnabled ?? true}
                                            onChange={(e) => handleFieldChange('addReportButtonEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Add Report Button (Mobile Reports Drawer)</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Show or hide the Add Report button inside the mobile reports drawer.
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Push Notification Prompt */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.pushNotificationPromptEnabled ?? true}
                                            onChange={(e) => handleFieldChange('pushNotificationPromptEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Push Notification Prompt</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Allow the mobile app to prompt users to enable push notifications.
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* App Update Prompt */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.appUpdatePromptEnabled ?? true}
                                            onChange={(e) => handleFieldChange('appUpdatePromptEnabled', e.target.checked)}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>App Update Prompt</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Allow the mobile app to prompt users when an app update is available.
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* User Management */}
                            <Typography fontWeight={500} mt={4} fontSize={18}>
                                User Management
                            </Typography>
                            <Typography fontSize={14} mb={3} color="text.secondary">
                                Define how you would like preregistered users to authenticate into your organization. The organization invite code will always be available. Optionally, you can prompt a user to provide their employee ID or email address in place of the invite code. This is helpful if you don't know the mobile phone number for users but know their Employee ID or email.
                            </Typography>
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                            <Grid maxWidth="sm" sx={{ width: 2/3 }}>
                                    <Typography fontWeight="600" fontSize={16}>User Identifier</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Define how you would like preregistered users to authenticate into your organization.
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                                    <Select
                                        {...getFieldProps('userUniqueIdentifier')}
                                        value={values?.userUniqueIdentifier || 'INVITECODE'}
                                        onChange={(e) => handleFieldChange('userUniqueIdentifier', e.target.value)}
                                        disabled={!canEditSettings}
                                        sx={{ width: 200 }}
                                        error={touched.userUniqueIdentifier && Boolean(errors.userUniqueIdentifier)}
                                    >
                                        <MenuItem value="INVITECODE">Invite Code</MenuItem>
                                        <MenuItem value="EMPLOYEEID">Employee ID</MenuItem>
                                        <MenuItem value="EMAILADDRESS">Email</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {touched.userUniqueIdentifier && errors.userUniqueIdentifier}
                                    </FormHelperText>
                                </Grid>
                            </Grid>

                            <Typography fontWeight={500} mt={4} fontSize={18}>
                                Check On Me
                            </Typography>
                            <Typography fontSize={14} mb={2} color="text.secondary">
                                Allow users in your organization to schedule timed safety check-ins.
                            </Typography>

                            <CheckOnMeFlowDiagram values={values} />

                            {/* Check On Me Enable */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.checkOnMeConfig === "Enabled"}
                                            onChange={(e) => {
                                                handleFieldChange('checkOnMeConfig', e.target.checked ? "Enabled": "Disabled");
                                                if (!e.target.checked) {
                                                    handleFieldChange('escalateCheckOnMe', false);
                                                    handleFieldChange('checkOnMeNotifications', "NONE");
                                                    handleFieldChange('checkOnMeRiskReportSurvey', false);
                                                    handleFieldChange('checkOnMeDispatchEnabled', false);
                                                }
                                            }}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Check On Me</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Enable the Check on Me feature for your organization. NOTE: This makes the feature available, but control of the feature is handled at the Group & Team level
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Check Out Reminder */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.checkoutReminderMessage ?? false}
                                            onChange={(e) => handleFieldChange('checkoutReminderMessage', e.target.checked)}
                                            disabled={!canEditSettings || values?.checkOnMeConfig !== "Enabled"}
                                        />
                                    }
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Check Out Reminder</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Send a reminder message 2 minutes before a Check On Me expires.
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Expiration Notification Method */}
                            <Grid container sx={{ width: 800, borderRadius: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3, gap: 2 }}>
                                <Grid item xs>
                                    <Typography fontWeight="600" fontSize={16}>Expiration Notification Method</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        How to notify the user when their Check On Me expires. "Message" sends a push or SMS; "Call" places an automated phone call.
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                                    <Select
                                        value={values?.expireNotifyMethod || 'MESSAGE'}
                                        onChange={(e) => handleFieldChange('expireNotifyMethod', e.target.value)}
                                        disabled={!canEditSettings || values?.checkOnMeConfig !== "Enabled"}
                                        sx={{ width: 200 }}
                                    >
                                        <MenuItem value="MESSAGE">Message</MenuItem>
                                        <MenuItem value="CALL">Call</MenuItem>
                                    </Select>
                                </Grid>
                            </Grid>

                            {/* Auto Escalate Check On Me */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.escalateCheckOnMe ?? false}
                                            onChange={(e) => handleFieldChange('escalateCheckOnMe', e.target.checked)}
                                            disabled={!canEditSettings || values?.checkOnMeConfig !== "Enabled"}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 2/3 }}>
                                    <Typography fontWeight="600" fontSize={16}>Set Timer to Auto Escalate Expired Check on Me</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        When a Check on Me expires, automatically escalate the alert to POM Dispatch after the grace period
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                                    <Select
                                        {...getFieldProps('escalateCheckOnMeTimeout')}
                                        disabled={!canEditSettings || values?.checkOnMeConfig === "Disabled" || !values?.escalateCheckOnMe}
                                        sx={{ width: 150 }}
                                        error={touched.escalateCheckOnMeTimeout && Boolean(errors.escalateCheckOnMeTimeout)}
                                        onChange={(e) => handleFieldChange('escalateCheckOnMeTimeout', e.target.value)}
                                        value={values?.escalateCheckOnMeTimeout || 1}
                                    >
                                        {[1,2,3,4,5,6,7,8,9,10].map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option} {option === 1 ? 'minute' : 'minutes'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {touched.escalateCheckOnMeTimeout && errors.escalateCheckOnMeTimeout}
                                    </FormHelperText>
                                </Grid>
                            </Grid>

                            {/* Notifications */}
                            <Grid container sx={{ width: 800, borderRadius: 2, padding: 2, border: 1, ascendDirection: "row", borderColor: "divider", mt: 3, gap: 2 }}>
                                <Grid item xs>
                                    <Typography fontWeight="600" fontSize={16}>Notifications</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Choose which Check on Me events your Team Leaders should be notified about
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ mt: 1, ml: 'auto' }}>
                                    <Select
                                        {...getFieldProps('checkOnMeNotifications')}
                                        value={values.checkOnMeNotifications || 'EXPIRED_ESCALATED'}
                                        onChange={(e) => handleFieldChange('checkOnMeNotifications', e.target.value)}
                                        disabled={!canEditSettings || values.checkOnMeConfig === "Disabled" || !values?.escalateCheckOnMe}
                                        sx={{ width: 320 }}
                                        error={touched.checkOnMeNotifications && Boolean(errors.checkOnMeNotifications)}
                                    >
                                        {notificationOptions.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {CHECK_ON_ME_WHEN[option] ?? option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {touched.checkOnMeNotifications && errors.checkOnMeNotifications}
                                    </FormHelperText>
                                </Grid>
                            </Grid>


                            {/* Visit Risk Review Survey */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.checkOnMeRiskReportSurvey ?? false}
                                            onChange={(e) => handleFieldChange('checkOnMeRiskReportSurvey', e.target.checked)}
                                            disabled={!canEditSettings || values.checkOnMeConfig !== "Enabled"}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Visit Risk Review Survey</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Ask users to fill out a survey after they have ended their Check on Me
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Typography fontWeight={500} mt={4} fontSize={18}>
                                Daily Shift
                            </Typography>
                            <Typography fontSize={14} mb={3} color="text.secondary">
                                Allow users in your organization to start a daily shift timer to track the start and end of their work day.
                            </Typography>

                            {/* Shift Enable - UI Only */}
                            <Grid container sx={{ width: 800, borderRadius: 2, spacing: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.shiftConfig === "Enabled"}
                                            onChange={(e) => {
                                                handleFieldChange("shiftConfig", e.target.checked ? "Enabled" : "Disabled");
                                                if (!e.target.checked) {
                                                    handleFieldChange("escalateShift", false);
                                                    handleFieldChange("shiftDispatchEnabled", false);
                                                }
                                            }}
                                            disabled={!canEditSettings}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid maxWidth="sm" sx={{ width: 1 }}>
                                    <Typography fontWeight="600" fontSize={16}>Shift</Typography>
                                    <Typography color="textSecondary" variant="body2">
                                        Enable the Shift Feature for your organization. NOTE: This makes the feature available, but control of the feature is handled at the Group & Team level
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Auto Escalate Shifts */}
                            <Grid container sx={{ width: 800, borderRadius: 2, padding: 2, border: 1, flexDirection: "row", borderColor: "divider", mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values?.escalateShift ?? false}
                                            onChange={(e) => handleFieldChange('escalateShift', e.target.checked)}
                                            disabled={!canEditSettings || values?.shiftConfig === "Disabled"}
                                        />
                                    } 
                                    label=""
                                    sx={{ my: "auto" }}
                                />
                                <Grid item sx={{ my: 'auto' }}>
                                    <Typography fontWeight="600" fontSize={16}>Set Timer to Auto Escalate Expired Shifts</Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ my: 'auto' }}>
                                        When a Shift expires, automatically escalate the alert to POM Dispatch
                                    </Typography>
                                </Grid>

                                <Grid item sx={{ mt: 1, mx: "auto" }}>
                                    <Select
                                        {...getFieldProps('escalateShiftTimeout')}
                                        disabled={!canEditSettings || values?.shiftConfig === "Disabled" || !values?.escalateShift}
                                        sx={{ width: 150 }}
                                        error={touched.escalateShiftTimeout && Boolean(errors.escalateShiftTimeout)}
                                        onChange={(e) => handleFieldChange('escalateShiftTimeout', e.target.value)} 
                                        value={values?.escalateShiftTimeout || 1} // Ensure controlled component
                                    >
                                        {[1,2,3,4,5,6,7,8,9,10].map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option} {option === 1 ? 'minute' : 'minutes'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {touched.escalateShiftTimeout && errors.escalateShiftTimeout}
                                    </FormHelperText>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Container>
            </When>

            {/* Change Organization Owner Modal */}
            <ChangeOrganizationOwnerModal
                visible={changeOwnerModalOpen}
                onClose={() => setChangeOwnerModalOpen(false)}
                currentOwner={details?.customer ? {
                    id: details.customer.id,
                    fullname: `${details.customer.contactFirstName || ''} ${details.customer.contactLastName || ''}`.trim(),
                    email: details.customer.contactEmail || '',
                    phone: details.customer.phoneNumber || '',
                    customerId: details.customer.id,
                    customer: details.customer,
                    pomMaster: false,
                    masquarade: false,
                    isFirstLogin: false,
                    subscriptionId: details.customer.subscriptionId,
                    lastActive: new Date(),
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    homeLocation: {} as any,
                    workLocation: {} as any,
                    password: null,
                    provider: null,
                    providerKey: null,
                } : undefined}
                onSuccess={async () => {
                    // Refresh local customer details to show the updated organization owner.
                    await refetchDetails();
                }}
            />
        </Page>
    );
};
